import React, {useCallback, useEffect, useState} from 'react';
import {App, Spin, Tooltip} from 'antd';
import {
    executeHedgeReport,
    getHedgeConfig,
    getHedgeExecutionByReport,
    getUserHedgeConfig,
} from '../../../../services/hedgeService';
import type {HedgeConfig, HedgeExecution, UserHedgeConfig} from '../../../../types/hedge';
import {useAuth} from '../../../../contexts/AuthContext';
import {MEMBERSHIP_REQUIRED} from '../../../../services/membershipService';

/** 执行状态中文文案 */
const STATUS_TEXT: Record<string, string> = {
    CALCULATED: '待执行',
    SKIPPED: '未触发',
    REJECTED: '风控拒绝',
    EXECUTING: '执行中',
    OPEN: '已完成对冲',
    PARTIAL_OPEN: '部分对冲',
    COMPENSATED: '已补偿退出',
    FAILED: '失败',
};

/** 币量格式化：≥1 保留 4 位小数，<1 保留 4 位有效数字 */
const formatQty = (qty?: number): string =>
    null == qty ? '--' : (Number(qty) >= 1 ? Number(qty).toFixed(4) : Number(qty).toPrecision(4));

/** 价格格式化（千分位，最多 8 位小数） */
const formatPrice = (price?: number): string =>
    null == price ? '--' : `$${Number(price).toLocaleString('en-US', {maximumFractionDigits: 8})}`;

/**
 * 研报内嵌对冲摘要条（紧凑粒度）：
 * 展示该研报触发的对冲交易对（主单/对冲单/对冲率）与执行状态；
 * 手动执行——「执行对冲」按钮仅在**最新一份研报 + 生成 30 分钟内 + 待执行状态**时可点击
 * （可执行性由后端判定，前端仅渲染按钮状态与原因）。
 */
export const HedgeSummaryBar: React.FC<{ reportId?: number }> = ({reportId}) => {
    const {message} = App.useApp();
    const {hasMemberLevel} = useAuth();
    const [config, setConfig] = useState<HedgeConfig | null>(null);
    /** 我的对冲配置（autoExecute 已迁用户级） */
    const [userConfig, setUserConfig] = useState<UserHedgeConfig | null>(null);
    const [execution, setExecution] = useState<HedgeExecution | null>(null);
    const [loading, setLoading] = useState(false);
    const [executing, setExecuting] = useState(false);

    const load = useCallback(async () => {
        if (null == reportId) {
            return;
        }
        setLoading(true);
        try {
            const [cfg, user, exec] = await Promise.all([
                getHedgeConfig(),
                getUserHedgeConfig(),
                getHedgeExecutionByReport(reportId),
            ]);
            setConfig(cfg);
            setUserConfig(user);
            setExecution(exec);
        } catch {
            setConfig(null);
            setUserConfig(null);
            setExecution(null);
        } finally {
            setLoading(false);
        }
    }, [reportId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleExecute = async () => {
        if (null == reportId || executing) {
            return;
        }
        setExecuting(true);
        try {
            const tip = await executeHedgeReport(reportId);
            message.success(tip ?? '已提交执行');
            await load();
        } catch (e: any) {
            message.error(e?.message ?? '执行失败');
        } finally {
            setExecuting(false);
        }
    };

    // 对冲需要专业级及以上（后端接口已按 PRO 强校验）：
    // 等级不足时整条不渲染——否则 403 会被 catch 吞成 config=null，误示「对冲功能未开启」
    if (!hasMemberLevel(MEMBERSHIP_REQUIRED.HEDGE)) {
        return null;
    }
    if (loading && null == execution) {
        return <div className="rr-hedge-bar"><Spin size="small"/></div>;
    }
    if (null == config || 1 !== Number(config.enabled)) {
        return (
            <div className="rr-hedge-bar rr-hedge-dim">
                对冲功能未开启（默认关闭；开启后新生成的研报将自动计算对冲交易对）
            </div>
        );
    }
    if (null == execution) {
        return (
            <div className="rr-hedge-bar rr-hedge-dim">
                该研报暂无对冲记录（功能开启后新生成的研报会自动计算）
            </div>
        );
    }

    const biasCls = 'bullish' === execution.bias ? 'rr-bull'
        : ('bearish' === execution.bias ? 'rr-bear' : 'rr-amber');
    const biasText = 'bullish' === execution.bias ? '▲偏多'
        : ('bearish' === execution.bias ? '▼偏空' : '●盘整');

    if (null == execution.pair) {
        return (
            <div className="rr-hedge-bar">
                <span className="rr-hedge-tag">对冲交易</span>
                <span className={`rr-hedge-bias ${biasCls}`}>{execution.symbol} {biasText}</span>
                <span className="rr-hedge-dim">未触发：{execution.skipReason ?? '--'}</span>
            </div>
        );
    }

    const pair = execution.pair;
    const mainIsSpot = 'SPOT' === pair.mainLeg;
    const spotText = `买现货 ${formatQty(pair.spotQty)} ${pair.symbol} @${formatPrice(pair.spotPrice)}`;
    const swapText = `空永续 ${formatQty(pair.swapQty)} ${pair.symbol} @${formatPrice(pair.swapPrice)}`;
    const executable = 1 === Number(execution.executable);
    const autoExecute = 1 === Number(userConfig?.autoExecute);

    return (
        <div className="rr-hedge-bar">
            <span className="rr-hedge-tag">对冲交易对</span>
            <span className={`rr-hedge-bias ${biasCls}`}>{execution.symbol} {biasText}</span>
            <span className="rr-hedge-leg">
                <span className="rr-hedge-role">主单</span>{mainIsSpot ? spotText : swapText}
            </span>
            <span className="rr-hedge-sep">·</span>
            <span className="rr-hedge-leg">
                <span className="rr-hedge-role">对冲单</span>{mainIsSpot ? swapText : spotText}
            </span>
            <span className="rr-hedge-sep">·</span>
            <span className="rr-hedge-ratio">对冲率 {Number(pair.hedgeRatio)}%</span>
            <span className="rr-hedge-sep">·</span>
            <span className="rr-hedge-status">{STATUS_TEXT[execution.execStatus] ?? execution.execStatus}</span>
            {/* 自动模式不暴露手动入口；后端原子抢占执行记录，避免重复提交。 */}
            {autoExecute ? (
                <span className="rr-hedge-exec-disabled">
                    {'AUTO' === execution.submitMode ? '自动执行' : '自动执行已开启'}
                </span>
            ) : executable ? (
                <button className="rr-hedge-exec-btn" disabled={executing} onClick={handleExecute}>
                    {executing ? '提交中…' : '执行对冲'}
                </button>
            ) : (
                <Tooltip title={execution.executableReason ?? '当前不可执行'}>
                    <span className="rr-hedge-exec-disabled">执行对冲</span>
                </Tooltip>
            )}
        </div>
    );
};
